import { FC, Fragment, useRef } from 'react';
import { PackageDetails as Package } from 'ts/package/interfaces/PackageDetails';
import { useTranslation } from 'react-i18next';
import { getCurrency, formatPriceCurrency, addDays } from 'utils/utils';
import { formatOccupancySrtByDeal, getDealType, getFlight } from 'utils/package';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import TicketBaggagePac from '../Book/TicketBaggagePac';
import ReactCountryFlag from 'react-country-flag';

interface TourDetailsProps {
  dealModel: Package;
  totalPrice: number;
  totalAvgPrice: number;
}

const TourDetails: FC<TourDetailsProps> = ({
  dealModel: { deal, options, arrivalAirport, departureAirport, additionalInfo },
  ...props
}) => {
  const [t] = useTranslation();
  const returnDate = useRef(
    addDays(+deal.dealLength, new Date(deal.departureDate)).toString()
  ).current;

  const flight = getFlight(options);

  return (
    <Fragment>
      <div className="default-shadow flight-details-item">
        <div className="title-underline text-lg mb-15">{t('package.details')}</div>
        <div className="flight-details-info">
          <div className="package-details-block">
            <span className="package-details-title">{t('flight.departure_date')}</span>
            <span className="package-details-text">
              {dateFormat(deal.departureDate, 'EEE - dd MMMM, yyyy')}
            </span>
          </div>
          <div className="package-details-block">
            <span className="package-details-title">{t('flight.return_date')}</span>
            <span className="package-details-text">
              {dateFormat(returnDate, 'EEE - dd MMMM, yyyy')}
            </span>
          </div>
          <div className="package-details-block">
            <span className="package-details-title">{t('package.nights')}</span>
            <span className="package-details-text">
              {deal.dealLength} {t('hotel.nights_up')}
            </span>
          </div>
          {!!departureAirport && (
            <div className="package-details-block">
              <span className="package-details-title">{t('flight.from')}</span>
              <span className="package-details-text">
                <ReactCountryFlag className="mr-5" svg countryCode={departureAirport.countryCode} />
                <span>
                  {departureAirport.airCityLang.desc} - {departureAirport.code}{' '}
                </span>
                <span className="text-xs text-light">{departureAirport.desc}</span>
              </span>
            </div>
          )}
          {!!arrivalAirport && (
            <div className="package-details-block">
              <span className="package-details-title">{t('flight.to')}</span>
              <span className="package-details-text">
                <ReactCountryFlag className="mr-5" svg countryCode={arrivalAirport.countryCode} />
                <span>
                  {arrivalAirport.airCityLang.desc} - {arrivalAirport.code}{' '}
                </span>
                <span className="text-xs text-light">{arrivalAirport.desc}</span>
              </span>
            </div>
          )}
          <div className="package-details-block">
            <span className="package-details-title">{t('package.package')}</span>
            <span className="package-details-text text-cap">
              <span>{deal.rowName}</span>
            </span>
          </div>
          {!!flight && (
            <Fragment>
              <div className="package-details-block">
                <span className="package-details-title">{t('filter.duration')}</span>
                <span className="package-details-text">
                  {flight.outbound.flightDuration}{' '}
                  {!!flight.inbound && `/ ${flight.inbound.flightDuration}`}
                </span>
              </div>
              <div className="package-details-block">
                <span className="package-details-title">{t('filter.airline')}</span>
                <span className="package-details-text">
                  {flight.outbound.airline} {!!flight.inbound && `/ ${flight.inbound.airline}`}
                </span>
              </div>
            </Fragment>
          )}

          <div className="package-details-block">
            <span className="package-details-title">{t('package.package_type')}</span>
            <span className="package-details-text text-cap">
              <span className="text-normal">
                ({additionalInfo.cityFullDescription || deal.dealDestination})
              </span>
              <span> {t(getDealType(deal.dealType, 1)?.toLowerCase())} </span>
              <span className="text-contrast">{deal.dealTitle?.toLowerCase()}</span>
            </span>
          </div>
          <div className="package-details-block">
            <span className="package-details-title">{t('package.occupancy')}</span>
            <span className="package-details-text">{formatOccupancySrtByDeal(deal)}</span>
          </div>
          <div className="package-details-block">
            <span className="package-details-title">{t('total')}</span>
            <span className="package-details-text">
              {formatPriceCurrency(props.totalPrice, getCurrency(deal.currency))}
            </span>
          </div>
          <div className="package-details-block">
            <span className="package-details-title">{t('flight.total_average_passenger')}</span>
            <span className="package-details-text text-lg text-contrast">
              {formatPriceCurrency(props.totalAvgPrice, getCurrency(deal.currency))}
            </span>
          </div>
        </div>
      </div>

      <div className="default-shadow flight-details-item pac-baggage display-flex">
        <TicketBaggagePac dealBaggage={deal?.dealBaggage} />
      </div>
    </Fragment>
  );
};

export default withErrorBoundary(TourDetails, 'TourDetails');
